(() => {
  const STORAGE_KEY = 'glacier-theme';
  const ROOT = document.documentElement;

  const normalise = (value) => (value === 'dark' ? 'dark' : 'light');

  const getTheme = () => normalise(ROOT.dataset.glacierTheme || 'light');

  const updateThemeColour = (theme) => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#101417' : '#fdfbf7');
  };

  const updateControls = (theme) => {
    document.querySelectorAll('[data-glacier-theme-toggle]').forEach((button) => {
      const dark = theme === 'dark';
      button.setAttribute('aria-pressed', dark ? 'true' : 'false');
      button.setAttribute('aria-label', dark ? 'Switch to light mode' : 'Switch to dark mode');
      button.setAttribute('title', dark ? 'Switch to light mode' : 'Switch to dark mode');
    });
  };

  const applyTheme = (theme, persist = true) => {
    const next = normalise(theme);
    ROOT.dataset.glacierTheme = next;
    updateThemeColour(next);
    updateControls(next);

    if (persist) {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch (_) {
        // Storage can be unavailable in private browsing; the toggle still works for the session.
      }
    }

    document.dispatchEvent(new CustomEvent('glacier:themechange', { detail: { theme: next } }));
  };

  const bindControls = (scope = document) => {
    scope.querySelectorAll('[data-glacier-theme-toggle]').forEach((button) => {
      if (button.dataset.glacierBound === 'true') return;
      button.dataset.glacierBound = 'true';
      button.addEventListener('click', () => {
        applyTheme(getTheme() === 'dark' ? 'light' : 'dark');
      });
    });
    updateControls(getTheme());
  };

  const updateStickyOffset = () => {
    const headerSections = document.querySelectorAll('.shopify-section-group-header-group');
    if (!headerSections.length) {
      ROOT.style.setProperty('--glacier-sticky-offset', '13rem');
      return;
    }

    const visibleBottom = Array.from(headerSections).reduce((lowestEdge, section) => {
      const rect = section.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return lowestEdge;
      return Math.max(lowestEdge, Math.min(window.innerHeight, rect.bottom));
    }, 0);

    ROOT.style.setProperty('--glacier-sticky-offset', `${Math.ceil(visibleBottom + 16)}px`);
  };

  let offsetFrame = 0;
  const scheduleStickyOffset = () => {
    if (offsetFrame) return;
    offsetFrame = window.requestAnimationFrame(() => {
      offsetFrame = 0;
      updateStickyOffset();
    });
  };

  const BEAM_SELECTOR = '.product-grid .product-card-wrapper, [data-glacier-beam-card]';
  const BOUND_BEAM_SELECTOR = '[data-glacier-beam-bound="true"]';
  const BEAM_ACTIVE_CLASS = 'glacier-card-beam-active';
  const BEAM_MIN_VISIBLE_RATIO = 0.18;
  const BEAM_THRESHOLDS = Array.from({ length: 11 }, (_, index) => index / 10);
  const beamCards = new Set();
  const beamCandidates = new Set();
  let beamObserver = null;
  let beamMutationObserver = null;
  let beamFrame = 0;

  const elementsWithin = (scope, selector) => {
    const elements = [];
    if (scope instanceof Element && scope.matches(selector)) elements.push(scope);
    if (scope?.querySelectorAll) elements.push(...scope.querySelectorAll(selector));
    return elements;
  };

  const getBeamLimit = () => {
    if (window.matchMedia('(max-width: 479px)').matches) return 1;
    if (window.matchMedia('(max-width: 989px)').matches) return 2;
    return 4;
  };

  const getVisibleBeamMetrics = (card) => {
    const rect = card.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;

    const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    const visibleRatio = (visibleWidth * visibleHeight) / (rect.width * rect.height);
    if (visibleRatio < BEAM_MIN_VISIBLE_RATIO) return null;

    const cardCenterX = rect.left + rect.width / 2;
    const cardCenterY = rect.top + rect.height / 2;
    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;
    const centerDistance = Math.hypot(
      (cardCenterX - viewportCenterX) / Math.max(window.innerWidth, 1),
      (cardCenterY - viewportCenterY) / Math.max(window.innerHeight, 1),
    );

    return { card, visibleRatio, centerDistance };
  };

  const updateProductBeams = () => {
    const source = beamObserver ? beamCandidates : beamCards;
    const ranked = Array.from(source)
      .map(getVisibleBeamMetrics)
      .filter(Boolean)
      .sort((first, second) => (
        second.visibleRatio - first.visibleRatio
        || first.centerDistance - second.centerDistance
      ));
    const activeCards = new Set(ranked.slice(0, getBeamLimit()).map(({ card }) => card));

    beamCards.forEach((card) => {
      const isActive = activeCards.has(card);
      card.classList.toggle(BEAM_ACTIVE_CLASS, isActive);
      card.classList.toggle('glacier-card-beam-paused', !isActive);
    });
  };

  const scheduleBeamSelection = () => {
    if (beamFrame) return;
    beamFrame = window.requestAnimationFrame(() => {
      beamFrame = 0;
      updateProductBeams();
    });
  };

  const refreshBeamPhases = () => {
    document.querySelectorAll('.product-grid, [data-glacier-beam-group]').forEach((grid) => {
      grid.querySelectorAll('.product-card-wrapper, [data-glacier-beam-card]').forEach((card, index) => {
        card.dataset.glacierBeamPhase = String((index % 4) + 1);
      });
    });
  };

  const bindProductBeams = (scope = document) => {
    const supportsIntersectionObserver = 'IntersectionObserver' in window;

    if (supportsIntersectionObserver && !beamObserver) {
      beamObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            beamCandidates.add(entry.target);
          } else {
            beamCandidates.delete(entry.target);
          }
        });
        scheduleBeamSelection();
      }, { threshold: BEAM_THRESHOLDS });
    }

    elementsWithin(scope, BEAM_SELECTOR).forEach((card) => {
      if (card.dataset.glacierBeamBound === 'true') return;
      card.dataset.glacierBeamBound = 'true';
      card.classList.add('glacier-card-beam-paused');
      beamCards.add(card);
      if (beamObserver) {
        beamObserver.observe(card);
      } else {
        beamCandidates.add(card);
      }
    });

    refreshBeamPhases();
    scheduleBeamSelection();
  };

  const unbindProductBeams = (scope) => {
    elementsWithin(scope, BOUND_BEAM_SELECTOR).forEach((card) => {
      if (beamObserver) beamObserver.unobserve(card);
      beamCards.delete(card);
      beamCandidates.delete(card);
      card.classList.remove(BEAM_ACTIVE_CLASS, 'glacier-card-beam-paused');
      delete card.dataset.glacierBeamPhase;
      delete card.dataset.glacierBeamBound;
    });
    scheduleBeamSelection();
  };

  const observeProductGridChanges = () => {
    if (beamMutationObserver || !document.body) return;

    beamMutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.removedNodes.forEach((node) => {
          if (node instanceof Element) unbindProductBeams(node);
        });
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) bindProductBeams(node);
        });
      });
    });

    beamMutationObserver.observe(document.body, { childList: true, subtree: true });
  };

  const updatePageVisibility = () => {
    ROOT.toggleAttribute('data-glacier-page-hidden', document.hidden);
  };

  const initialise = () => {
    let saved = null;
    try {
      saved = window.localStorage.getItem(STORAGE_KEY);
    } catch (_) {
      saved = null;
    }

    if (saved !== 'light' && saved !== 'dark') {
      const control = document.querySelector('[data-glacier-theme-toggle]');
      const configured = ROOT.dataset.glacierDefaultTheme || control?.dataset.glacierDefaultTheme || 'light';
      const initial = configured === 'system'
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : configured;
      applyTheme(initial, false);
    } else {
      applyTheme(saved, false);
    }

    bindControls();
    bindProductBeams();
    observeProductGridChanges();
    updatePageVisibility();
    updateStickyOffset();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  window.addEventListener('resize', scheduleStickyOffset, { passive: true });
  window.addEventListener('resize', scheduleBeamSelection, { passive: true });
  window.addEventListener('scroll', scheduleStickyOffset, { passive: true });
  window.addEventListener('scroll', scheduleBeamSelection, { passive: true });
  window.addEventListener('load', scheduleStickyOffset, { once: true });
  window.addEventListener('load', scheduleBeamSelection, { once: true });
  document.addEventListener('visibilitychange', updatePageVisibility);

  document.addEventListener('shopify:section:load', (event) => {
    bindControls(event.target);
    bindProductBeams(event.target);
    scheduleBeamSelection();
    scheduleStickyOffset();
  });
})();
